<template>
    <v-autocomplete
        v-model="selectedData"
        :items="resolvedItems"
        :item-text="text"
        :item-value="value"
        :label="label"
        :rules="effectiveRules"
        :prepend-inner-icon="icon"
        :disabled="disabled"
        :readonly="readOnly"
        :clearable="!readOnly"
        :open-on-clear="false"
        :loading="lazy ? lazyLoading || resolvingSelection : false"
        :no-filter="lazy"
        v-bind="autocompleteBindings"
        class="merus-select"
        multiple
        @change="updateSelectedItems"
        @click:clear="onClear"
        @focus="onReadOnlyFocus"
        @click="onReadOnlyClick"
        @update:search-input="onSearchInput"
    >
        <template v-slot:prepend-item v-if="canToggleAll && !readOnly">
            <v-list-item
                ripple
                @mousedown.prevent
                @click="toggleAll"
            >
                <v-list-item-action>
                    <v-icon :color="toggleAllIconColor">
                        {{ selectIcon }}
                    </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                    <v-list-item-title>
                        {{ toggleAllLabel }}
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-2"></v-divider>
        </template>

        <template v-slot:item="{ item, attrs }">
            <!-- v-show (not v-if): empty slot crashes VSelectList on slot.length -->
            <v-list-item
                v-show="!isAllMatchingPlaceholder(item)"
                v-bind="attrs"
                :disabled="!!item.disabled"
                @mousedown.prevent
                @click.stop="toggleItem(item)"
            >
                <v-list-item-action>
                    <v-icon :color="item.invalid || item.disabled ? 'error' : (isItemSelected(item) ? 'indigo darken-4' : '')">
                        {{ isItemSelected(item) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                    </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                    <v-list-item-title :class="{ 'error--text': item.invalid || item.disabled }">
                        {{ getItemLabel(item) }}
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>
        </template>

        <template v-slot:append-item v-if="lazy">
            <v-list-item
                v-if="lazyHasMore || lazyLoadingMore"
                v-intersect="onLazyEndIntersect"
                dense
                class="merus-select__more"
                @mousedown.prevent
                @click="loadMoreLazyItems"
            >
                <v-list-item-content>
                    <v-list-item-subtitle>
                        <v-progress-circular v-if="lazyLoadingMore" indeterminate size="14" width="2" class="mr-2"></v-progress-circular>
                        {{ lazyFooterLabel }}
                    </v-list-item-subtitle>
                </v-list-item-content>
            </v-list-item>
        </template>

        <template v-slot:selection="{ item, index }">
            <template v-if="internalAllMatching && lazy">
                <v-chip
                    v-if="index === 0"
                    class="grey--text text-caption"
                    small
                >
                    <span>{{ allMatchingSelectionLabel }}</span>
                </v-chip>
            </template>
            <template v-else>
                <span
                    v-if="index <= maxItems - 1"
                    style="margin-right: 5px;"
                    :class="{ 'error--text': item.invalid || item.disabled }"
                >{{ getItemLabel(item) }}</span>
                <v-chip
                    v-if="index === maxItems"
                    class="grey--text text-caption"
                >
                    <span>(+{{ selectedData.length - maxItems }} {{ $t('operation.others') }})</span>
                </v-chip>
            </template>
        </template>
    </v-autocomplete>
</template>

<script>
import Intersect from 'vuetify/lib/directives/intersect';
import lazySelectMixin from './lazySelectMixin';
import './merusSelect.css';

export default {
    name: 'MultiplaEscolha',
    directives: { Intersect },
    // Vue 2 aplicaria os atributos extras (outlined, dense, prepend-icon...) só no elemento DOM raiz;
    // eles precisam chegar ao v-autocomplete como props.
    inheritAttrs: false,
    mixins: [lazySelectMixin],
    props: {
        items: {
            type: Array,
            default: () => [],
            validator(val) {
                return Array.isArray(val);
            },
        },
        lazy: {
            type: Boolean,
            default: false,
        },
        text: {
            type: String,
            required: true,
        },
        value: {
            type: String,
            required: true,
        },
        /**
         * Rótulo alternativo do item (lista e seleção), quando um único campo
         * não basta — p.ex. unidade exibida como "código - nome".
         * Não altera a busca, que continua usando `text`.
         */
        itemLabel: {
            type: Function,
            default: null,
        },
        label: {
            type: String,
            required: false,
            default() {
                return this.$t('system.choose');
            },
        },
        rules: {
            type: Array,
            required: false,
            default() {
                return [];
            },
        },
        icon: {
            type: String,
            required: false,
            default() {
                return null;
            },
        },
        disabled: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        readOnly: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        maxItems: {
            type: Number,
            required: false,
            default() {
                return 3;
            },
        },
        canToggleAll: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        /**
         * Limite de itens que o pai aceita (ele corta o excedente). Com o limite atingido,
         * "Todos" passa a limpar a seleção em vez de tentar completar o total de novo.
         */
        maxSelection: {
            type: Number,
            default: null,
        },
        /** Optional .sync — true when lazy "Todos" selects all matching filters. */
        allMatchingSelected: {
            type: Boolean,
            default: false,
        },
        /**
         * Selected value ids (.sync). Without this prop, parent→child hydration
         * never reaches selectedData (only child→parent emits worked).
         */
        selectedItems: {
            type: Array,
            default: null,
        },
    },
    data: () => ({
        selectedData: [],
        lockedData: [],
        lazyItems: [],
        /** Páginas já buscadas na pesquisa atual, antes do filtro/merge. */
        fetchedLazyItems: [],
        internalAllMatching: false,
        resolvingSelection: false,
        /** Placeholder row so selection slot can render the "Todos" chip. */
        allMatchingPlaceholder: null,
    }),
    computed: {
        /**
         * Lazy mode may also receive static `items` (e.g. "Sem Unidade") prepended
         * and kept when selected or when the search matches their label.
         */
        resolvedItems() {
            if (!this.lazy) return this.items;
            const lazy = Array.isArray(this.lazyItems) ? this.lazyItems : [];
            const extras = Array.isArray(this.items) ? this.items : [];
            const key = this.value;
            const textKey = this.text;
            const selected = Array.isArray(this.selectedData) ? this.selectedData : [];
            const search = (this.lazySearch || '').trim().toLowerCase();
            const seen = new Set(lazy.map((i) => i && i[key]));

            const prepend = extras.filter((item) => {
                if (item == null || item[key] == null || seen.has(item[key])) return false;
                if (this.internalAllMatching || selected.includes(item[key])) return true;
                if (!search) return true;
                return String(item[textKey] != null ? item[textKey] : '')
                    .toLowerCase()
                    .includes(search);
            });

            let list = prepend.length ? [...prepend, ...lazy] : lazy;
            // Selecoes vindas do parent (cache) ainda nao trazidas pela pagina atual:
            // sem isso o chip so ganha rotulo depois da proxima busca.
            if (!this.internalAllMatching && selected.length) {
                const present = new Set(
                    list
                        .filter((item) => item != null && item[key] != null && item[key] !== '')
                        .map((item) => String(item[key]))
                );
                const missing = selected
                    .filter((id) => id != null && id !== '' && !present.has(String(id)))
                    .map((id) => this.getCachedSelectedItem(id))
                    .filter(Boolean);
                if (missing.length) {
                    list = [...list, ...missing];
                }
            }
            // Sentinel so Vuetify can render the "Todos" selection chip.
            if (this.internalAllMatching && this.allMatchingPlaceholder) {
                list = [this.allMatchingPlaceholder, ...list];
            }
            return list;
        },
        autocompleteBindings() {
            return { ...this.$attrs, ...this.lazySearchBindings };
        },
        lazySearchBindings() {
            if (!this.lazy) return {};
            return { searchInput: this.lazySearch };
        },
        selectableItems() {
            return (this.resolvedItems || []).filter(
                (item) => item && !item.disabled && !this.isAllMatchingPlaceholder(item)
            );
        },
        selectableExtras() {
            if (!this.lazy || !Array.isArray(this.items)) return [];
            return this.items.filter((item) => item && !item.disabled);
        },
        matchingTotalDisplay() {
            const lazyPart = this.lazyTotal != null && Number.isFinite(Number(this.lazyTotal))
                ? Number(this.lazyTotal)
                : null;
            const extrasCount = this.selectableExtras.length;
            if (lazyPart == null) {
                return extrasCount > 0 ? extrasCount : null;
            }
            // Extras are static and not part of lazy total; count them when present.
            return lazyPart + extrasCount;
        },
        lazyFooterLabel() {
            if (this.lazyLoadingMore) return this.$t('system.lazyLoadingMore');
            if (this.lazyTotal != null) {
                return this.$t('system.lazyShowingOf', {
                    loaded: this.lazyLoadedCount,
                    total: this.lazyTotal,
                });
            }
            return this.$t('system.lazyLoadMore');
        },
        toggleAllLabel() {
            const base = this.$t('operation.all');
            if (this.lazy && this.matchingTotalDisplay != null) {
                return `${base} (${this.matchingTotalDisplay})`;
            }
            return base;
        },
        allMatchingSelectionLabel() {
            const base = this.$t('operation.all');
            if (this.matchingTotalDisplay != null) {
                return `${base} (${this.matchingTotalDisplay})`;
            }
            return base;
        },
        toggleAllIconColor() {
            if (this.lazy && this.internalAllMatching) return 'indigo darken-4';
            if (this.selectableItems.length > 0) return 'indigo darken-4';
            return '';
        },
        selectIcon() {
            if (this.lazy && this.internalAllMatching) {
                return 'mdi-close-box';
            }
            if (this.isSelectionFull()) {
                return 'mdi-close-box';
            }
            if (this.selectedData.length > 0) return 'mdi-minus-box';
            return 'mdi-checkbox-blank-outline';
        },
        effectiveRules() {
            const base = Array.isArray(this.rules) ? this.rules : [];
            if (!this.lazy) return base;
            return base.map((rule) => {
                if (typeof rule !== 'function') return rule;
                return (val) => {
                    if (this.internalAllMatching && this.matchingTotalDisplay > 0) {
                        return true;
                    }
                    return rule(val);
                };
            });
        },
    },
    watch: {
        allMatchingSelected: {
            immediate: true,
            handler(val) {
                if (!!val === this.internalAllMatching) return;
                this.internalAllMatching = !!val;
                this.syncAllMatchingPlaceholder();
            },
        },
        selectedItems: {
            immediate: true,
            handler(val) {
                // Uncontrolled until parent binds selected-items / .sync.
                if (val == null) return;
                if (this.internalAllMatching) return;
                const next = Array.isArray(val) ? [...val] : [];
                if (this.isSameSelection(next, this.selectedData)) return;
                this.selectedData = next;
                this.lockedData = [...next];
                if (this.lazy && Array.isArray(this.items) && this.items.length) {
                    this.cacheSelectedItems(this.items, this.value);
                }
            },
        },
        items: {
            deep: false,
            handler(val) {
                if (!this.lazy || !Array.isArray(val) || !val.length) return;
                if (!Array.isArray(this.selectedData) || !this.selectedData.length) return;
                this.cacheSelectedItems(val, this.value);
            },
        },
        selectedData: {
            handler(val) {
                if (this.lazy && Array.isArray(val)) {
                    this.cacheSelectedItems(
                        this.resolvedItems.filter((item) => val.includes(item[this.value])),
                        this.value
                    );
                }
                if (!this.readOnly) return;
                if (!this.isSameSelection(val, this.lockedData)) {
                    this.$nextTick(() => {
                        this.selectedData = [...this.lockedData];
                    });
                }
            },
            deep: true,
        },
    },
    created() {
        if (this.lazy && !this.getUrl) {
            console.error('[MultiplaEscolha] getUrl is required when lazy is true');
        }
    },
    mounted() {
        if (this.lazy) {
            this.loadLazyItems(this.lazySearch);
        }
    },
    methods: {
        onLazyFiltersChanged() {
            this.clearAllMatchingMode();
        },
        setAllMatching(val) {
            const next = !!val;
            this.internalAllMatching = next;
            this.syncAllMatchingPlaceholder();
            this.$emit('update:allMatchingSelected', next);
        },
        clearAllMatchingMode() {
            if (!this.internalAllMatching) return;
            this.setAllMatching(false);
            this.selectedData = [];
            this.$emit('update:selectedItems', this.selectedData);
        },
        syncAllMatchingPlaceholder() {
            if (!this.lazy || !this.internalAllMatching) {
                if (this.allMatchingPlaceholder) {
                    const key = this.allMatchingPlaceholder[this.value];
                    if (Array.isArray(this.selectedData) && this.selectedData.includes(key)) {
                        this.selectedData = this.selectedData.filter((v) => v !== key);
                    }
                }
                this.allMatchingPlaceholder = null;
                return;
            }
            const placeholderKey = '__all_matching__';
            this.allMatchingPlaceholder = {
                [this.value]: placeholderKey,
                [this.text]: this.allMatchingSelectionLabel,
            };
            // Keep one sentinel value so Vuetify renders the selection slot.
            this.selectedData = [placeholderKey];
            // Do not emit sentinel to parent — parents use resolveSelection on confirm.
            this.$emit('update:selectedItems', []);
        },
        onSearchInput(val) {
            if (!this.lazy) return;
            const prev = this.lazySearch;
            const next = val == null ? '' : String(val);
            if (prev !== next && this.internalAllMatching) {
                this.clearAllMatchingMode();
            }
            this.loadLazyItems(val);
        },
        loadLazyItems(val) {
            this.fetchLazyItems(val, {
                onSuccess: (fetched) => {
                    this.fetchedLazyItems = fetched;
                    this.refreshLazyItems();
                    if (this.internalAllMatching) {
                        this.syncAllMatchingPlaceholder();
                    }
                },
            });
        },
        loadMoreLazyItems() {
            if (!this.lazy) return;
            this.fetchMoreLazyItems({
                onSuccess: (fetched) => {
                    const before = this.lazyItems.length;
                    this.fetchedLazyItems = this.appendUniqueLazyItems(this.fetchedLazyItems, fetched, this.value);
                    this.refreshLazyItems();
                    // Página sem itens novos (repetidos ou barrados pelo filtro): o rodapé
                    // segue visível e o intersect não dispara de novo.
                    if (this.lazyItems.length === before) {
                        this.$nextTick(() => this.loadMoreLazyItems());
                    }
                },
            });
        },
        refreshLazyItems() {
            const filtered = this.applyLazyItemFilter(this.fetchedLazyItems);
            const selectedForMerge = this.internalAllMatching
                ? []
                : this.selectedData;
            this.lazyItems = this.mergeLazyItems(filtered, selectedForMerge, this.value);
        },
        isSameSelection(a, b) {
            const left = Array.isArray(a) ? a : [];
            const right = Array.isArray(b) ? b : [];
            if (left.length !== right.length) return false;
            return left.every((item) => right.includes(item));
        },
        restoreLockedSelection() {
            this.selectedData = [...this.lockedData];
        },
        onClear() {
            if (this.readOnly) {
                this.restoreLockedSelection();
                return;
            }
            this.setAllMatching(false);
            this.selectedData = [];
            this.$emit('update:selectedItems', this.selectedData);
        },
        onReadOnlyFocus(e) {
            if (!this.readOnly || !e || !e.target) return;
            e.target.blur();
        },
        onReadOnlyClick(e) {
            if (!this.readOnly) return;
            if (e) {
                e.stopPropagation();
                if (e.target && typeof e.target.blur === 'function') {
                    e.target.blur();
                }
            }
        },
        isAllMatchingPlaceholder(item) {
            if (!item || !this.allMatchingPlaceholder) return false;
            return item[this.value] === this.allMatchingPlaceholder[this.value];
        },
        getItemLabel(item) {
            if (!item || typeof item !== 'object') return '';
            if (typeof this.itemLabel === 'function' && !this.isAllMatchingPlaceholder(item)) {
                const custom = this.itemLabel(item);
                if (custom != null && String(custom).trim() !== '') {
                    return String(custom);
                }
            }
            const key = this.text;
            if (key && item[key] != null && String(item[key]).trim() !== '') {
                return String(item[key]);
            }
            if (item.nome_linha != null && String(item.nome_linha).trim() !== '') {
                return String(item.nome_linha);
            }
            if (item.cod_linha != null && String(item.cod_linha).trim() !== '') {
                return String(item.cod_linha);
            }
            return '';
        },
        isItemSelected(item) {
            if (!item || this.isAllMatchingPlaceholder(item)) return false;
            if (this.lazy && this.internalAllMatching) return true;
            return (this.selectedData || []).includes(item[this.value]);
        },
        /** Todos os selecionáveis marcados, ou o máximo permitido (`maxSelection`) já atingido. */
        isSelectionFull() {
            const selectableValues = this.selectableItems.map((v) => v[this.value]);
            if (!selectableValues.length) return false;
            const selected = new Set(this.selectedData || []);
            const selectedCount = selectableValues.filter((value) => selected.has(value)).length;
            const limit = this.maxSelection > 0
                ? Math.min(this.maxSelection, selectableValues.length)
                : selectableValues.length;
            return selectedCount >= limit;
        },
        filterSelectableValues(values) {
            const selectable = new Set(this.selectableItems.map((item) => item[this.value]));
            return (Array.isArray(values) ? values : []).filter((id) => selectable.has(id));
        },
        toggleItem(item) {
            if (this.readOnly || !item || item.disabled) return;

            // Leaving "all matching" → explicit page selection (visible minus/plus clicked).
            if (this.lazy && this.internalAllMatching) {
                this.setAllMatching(false);
                const key = item[this.value];
                const visible = this.selectableItems.map((v) => v[this.value]);
                this.selectedData = visible.filter((id) => id !== key);
                this.$emit('update:selectedItems', this.selectedData);
                return;
            }

            const key = item[this.value];
            const current = Array.isArray(this.selectedData) ? [...this.selectedData] : [];
            const idx = current.indexOf(key);
            if (idx >= 0) {
                current.splice(idx, 1);
            } else {
                current.push(key);
            }
            this.selectedData = current;
            this.$emit('update:selectedItems', this.selectedData);
        },
        toggleAll() {
            if (this.readOnly) return;
            this.$nextTick(() => {
                if (this.lazy) {
                    if (this.internalAllMatching) {
                        this.setAllMatching(false);
                        this.selectedData = [];
                        this.$emit('update:selectedItems', this.selectedData);
                    } else {
                        this.setAllMatching(true);
                    }
                    return;
                }

                if (this.isSelectionFull()) {
                    this.selectedData = [];
                } else {
                    this.selectedData = this.selectableItems.map((v) => v[this.value]);
                }
                this.$emit('update:selectedItems', this.selectedData);
            });
        },
        updateSelectedItems() {
            if (this.readOnly) {
                this.restoreLockedSelection();
                return;
            }
            if (this.lazy && this.internalAllMatching) {
                // Ignore Vuetify chip removals while in all-matching mode except clear.
                this.syncAllMatchingPlaceholder();
                return;
            }
            const filtered = this.filterSelectableValues(this.selectedData);
            if (filtered.length !== (this.selectedData || []).length) {
                this.selectedData = filtered;
            }
            this.$emit('update:selectedItems', this.selectedData);
        },
        /**
         * Resolve selected value keys. When all-matching is on, paginates the lazy
         * endpoint with current filters and returns every matching id.
         * @param {{ pageSize?: number, requestData?: object }} [options]
         * @returns {Promise<Array>}
         */
        async resolveSelection(options = {}) {
            const placeholderKey = this.allMatchingPlaceholder
                ? this.allMatchingPlaceholder[this.value]
                : '__all_matching__';
            if (!this.lazy || !this.internalAllMatching) {
                return (Array.isArray(this.selectedData) ? this.selectedData : [])
                    .filter((id) => id !== placeholderKey);
            }

            this.resolvingSelection = true;
            try {
                const { items } = await this.fetchAllLazyItems({
                    search: this.lazySearch,
                    pageSize: options.pageSize,
                    requestDataOverride: options.requestData != null ? options.requestData : null,
                });
                const key = this.value;
                const ids = [];
                const seen = new Set();

                this.selectableExtras.forEach((item) => {
                    const id = item[key];
                    if (id == null || id === '') return;
                    const s = String(id);
                    if (seen.has(s)) return;
                    seen.add(s);
                    ids.push(id);
                });

                (items || []).forEach((item) => {
                    if (!item || item.disabled) return;
                    const id = item[key];
                    if (id == null || id === '') return;
                    const s = String(id);
                    if (seen.has(s)) return;
                    seen.add(s);
                    ids.push(id);
                });

                this.cacheSelectedItems(items || [], key);
                return ids;
            } finally {
                this.resolvingSelection = false;
            }
        },
        /**
         * Resolve full item objects for the current selection (hydrate on confirm).
         * @param {{ pageSize?: number, requestData?: object }} [options]
         * @returns {Promise<object[]>}
         */
        async resolveSelectionItems(options = {}) {
            if (!this.lazy || !this.internalAllMatching) {
                const values = Array.isArray(this.selectedData) ? this.selectedData : [];
                return values
                    .map((id) => {
                        const fromResolved = (this.resolvedItems || []).find(
                            (item) => item && item[this.value] === id
                        );
                        return fromResolved || this.getCachedSelectedItem(id);
                    })
                    .filter(Boolean);
            }

            this.resolvingSelection = true;
            try {
                const { items } = await this.fetchAllLazyItems({
                    search: this.lazySearch,
                    pageSize: options.pageSize,
                    requestDataOverride: options.requestData != null ? options.requestData : null,
                });
                const key = this.value;
                const result = [];
                const seen = new Set();

                this.selectableExtras.forEach((item) => {
                    const id = item[key];
                    if (id == null || id === '') return;
                    const s = String(id);
                    if (seen.has(s)) return;
                    seen.add(s);
                    result.push(item);
                });

                (items || []).forEach((item) => {
                    if (!item || item.disabled) return;
                    const id = item[key];
                    if (id == null || id === '') return;
                    const s = String(id);
                    if (seen.has(s)) return;
                    seen.add(s);
                    result.push(item);
                });

                this.cacheSelectedItems(result, key);
                return result;
            } finally {
                this.resolvingSelection = false;
            }
        },
        resetData() {
            this.lockedData = [];
            this.selectedData = [];
            this.selectedItemCache = {};
            this.setAllMatching(false);
            this.lazyTotal = null;
        },
        setData(data) {
            try {
                this.setAllMatching(false);
                const next = Array.isArray(data) ? [...data] : [];
                this.lockedData = [...next];
                this.selectedData = [...next];
            } catch {
                this.$emit('error', this.$t('system.messages.notPossibleUpdate'));
            }
        },
        setSelectedItems(items) {
            try {
                this.setAllMatching(false);
                const next = Array.isArray(items) ? [...items] : [];
                this.lockedData = [...next];
                this.selectedData = [...next];
                if (!this.readOnly) {
                    this.$emit('update:selectedItems', this.selectedData);
                }
            } catch (error) {
                this.$emit('error', this.$t('system.messages.notPossibleUpdate'));
            }
        },
    },
};
</script>
